import React, {useState, useEffect} from 'react';
import {Action, view} from '@sail/ui';
import {SearchItem} from 'src/v2/components/walletSelect/SearchItem';
import {WalletGrid} from 'src/v2/components/walletSelect/WalletGrid';
import {WalletSelectList} from 'src/v2/components/walletSelect/walletList/WalletSelectList';
import {ChipButton, LinkButton} from 'src/v2/components/common';
import {useIsAnyCryptoWalletConnectFlow} from 'src/v2/components/anyCrypto/useIsAnyCryptoWalletConnectFlow';
import {setValue, useSelector} from 'src/v2/state/store';
import {useWalletPress} from 'src/v2/components/walletSelect/useWalletPress';
import {qrCode} from '@sail/icons/react/Icon';
import type {PaymentOption} from 'src/v2/state/types';

import {message, useIntl} from '@sail/intl';

interface Props {
  walletOptions: PaymentOption[];
  onSearchPress: () => void;
}

const messages = {
  useConnectedWallets: message({
    id: 'crypto.payins.v2.wallet_select_grid.back',
    description: 'Button text to go back to the connected wallets screen',
    defaultMessage: 'Use connected wallets',
  }),

  directDeposit: message({
    id: 'crypto.payins.v2.wallet_select.direct_deposit',

    description:
      'Label for the direct deposit via QR code option in the wallet select UI',

    defaultMessage: 'Direct deposit via QR code',
  }),
};

const ALLOWED_WALLETS = [
  'Coinbase Wallet',
  'Phantom',
  'MetaMask',
  'trust',
  'okxwallet',
  'rainbow',
  'ledger',
];

// TODO (stellacai): remove preferred placement for Crypto.com at end of Q1 2026
const ALLOWED_WALLETS_WITH_EXCHANGE = [
  'Coinbase Wallet',
  'Phantom',
  'MetaMask',
  'cryptoDotCom',
  'trust',
  'okxwallet',
  'ledger',
];

export const WalletSelectGrid = ({walletOptions, onSearchPress}: Props) => {
  const intl = useIntl();
  const exchangeFeatureFlagEnabled = useSelector(
    (state) => state.featureFlags.enable_crypto_payins_pay_from_exchange,
  );
  const directDepositEnabled = useSelector(
    (state) =>
      state.featureFlags.enable_crypto_payins_direct_deposit &&
      state.settlementPartner === 'bridge' &&
      !state.setupFutureUsage,
  );
  const directDepositAnyCryptoFlowEnabled = useSelector(
    (state) => state.featureFlags.enable_direct_deposit_any_crypto_flow,
  );
  const isAnyCryptoWalletConnectFlow = useIsAnyCryptoWalletConnectFlow();

  const directDepositSupportedCurrencies = useSelector(
    (state) => state.directDepositSupportedCurrencies,
  );
  const selectedToken = useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );

  const setupFutureUsage = useSelector((state) => state.setupFutureUsage);
  const exchangePaymentsEnabled =
    !setupFutureUsage && exchangeFeatureFlagEnabled;

  const defaultWallets = exchangePaymentsEnabled
    ? ALLOWED_WALLETS_WITH_EXCHANGE
    : ALLOWED_WALLETS;

  const filteredWalletOptions = walletOptions
    .filter((walletOption) => {
      const walletKey =
        walletOption.provider === 'WalletConnect' ||
        walletOption.provider === 'Exchange'
          ? walletOption.key
          : walletOption.provider;
      return defaultWallets.includes(walletKey);
    })
    .sort((a, b) => {
      const getWalletKey = (walletOption: PaymentOption) =>
        walletOption.provider === 'WalletConnect' ||
        walletOption.provider === 'Exchange'
          ? walletOption.key
          : walletOption.provider;

      const indexA = defaultWallets.indexOf(getWalletKey(a));
      const indexB = defaultWallets.indexOf(getWalletKey(b));

      return indexA - indexB;
    });

  const connectedWallets = useSelector(
    (state) => state.walletConnection.connectedWallets,
  );
  const [contentVisible, setContentVisible] = useState(false);

  const showUseConnectedWalletsButton = connectedWallets.length > 0;
  const showDirectDeposit =
    directDepositEnabled || isAnyCryptoWalletConnectFlow;

  const handleWalletPress = useWalletPress(
    isAnyCryptoWalletConnectFlow ? 'wallet_list' : 'wallet_grid',
  );

  const handleDirectDepositClick = () => {
    if (isAnyCryptoWalletConnectFlow) {
      const networks = directDepositSupportedCurrencies
        .filter((c) => c.assetCode === selectedToken)
        .map((c) => c.currencyNetwork);

      if (networks.length > 1) {
        setValue((state) => {
          state.anyCryptoDeposit.selectedNetwork = null;
          state.screen = 'ANY_CRYPTO_NETWORK_SELECT';
        });
      } else {
        setValue((state) => {
          state.anyCryptoDeposit.selectedNetwork = networks[0] ?? null;
          state.anyCryptoDeposit.quote = {
            depositAddress: null,
            inputAmount: null,
            inputChain: null,
            inputCurrency: null,
          };
          state.screen = 'ANY_CRYPTO_REFUND';
        });
      }
      return;
    }

    setValue((state) => {
      state.screen = directDepositAnyCryptoFlowEnabled
        ? 'TOKEN_SELECT'
        : 'DIRECT_DEPOSIT';
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => setContentVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleConnectedWalletPress = () => {
    setValue((state) => {
      state.screen = 'WALLET_SWITCH';
    });
  };

  if (isAnyCryptoWalletConnectFlow) {
    return (
      <view.div
        css={{
          stack: 'y',
          gapY: 'space.150',
          opacity: contentVisible ? 1 : 0,
          transition: 'opacity 100ms ease-in-out',
        }}
      >
        <WalletSelectList
          walletOptions={walletOptions}
          defaultWalletOptions={filteredWalletOptions.slice(0, 5)}
          onWalletPress={handleWalletPress}
          onDirectDepositPress={
            showDirectDeposit ? handleDirectDepositClick : undefined
          }
          onConnectedWalletPress={
            showUseConnectedWalletsButton
              ? handleConnectedWalletPress
              : undefined
          }
        />
      </view.div>
    );
  }

  return (
    <view.div
      css={{
        stack: 'y',
        gapY: 'space.150',
        alignX: 'center',
        opacity: contentVisible ? 1 : 0,
        transition: 'opacity 100ms ease-in-out',
      }}
    >
      <view.div
        css={{
          stack: 'x',
          width: 'fill',
          minTileWidth: '76px',
          minTileHeight: '104px',
          alignX: 'center',
          alignY: 'top',
          gapY: 'space.150',
        }}
      >
        <Action onPress={onSearchPress}>
          <SearchItem />
        </Action>
        <WalletGrid
          walletOptions={filteredWalletOptions}
          onWalletPress={handleWalletPress}
        />
      </view.div>
      {showDirectDeposit ? (
        <ChipButton
          icon={qrCode}
          onClick={handleDirectDepositClick}
          label={intl.formatMessage(messages.directDeposit)}
        />
      ) : null}
      {showUseConnectedWalletsButton ? (
        <LinkButton onClick={handleConnectedWalletPress}>
          {intl.formatMessage(messages.useConnectedWallets)}
        </LinkButton>
      ) : null}
    </view.div>
  );
};
